import { useEffect, useState } from "react";
import type { Enabler, ObjectiveGroup } from "../types";
import { ChevronIcon } from "./Icons";
import { HighlightedText } from "./HighlightedText";
import { ResourceCard } from "./ResourceCard";

const RECOMMENDATION_TIERS = ["gold", "silver", "bronze"] as const;

type ObjectiveCardProps = {
  objective: ObjectiveGroup;
  enablersByCode: Map<string, Enabler>;
  defaultOpen?: boolean;
  searchTerm?: string;
};

export function ObjectiveCard({ objective, enablersByCode, defaultOpen = false, searchTerm = "" }: ObjectiveCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen, objective.id]);

  const panelId = `${objective.id}-resources`;
  const topRecommendedIds = new Set(objective.topRecommendedResources.map((resource) => resource.id));
  const remainingResources = objective.resources.filter((resource) => !topRecommendedIds.has(resource.id));

  return (
    <article className="objective-card">
      <button
        className="objective-card__button"
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="objective-card__copy">
          <span className="objective-card__meta">
            <span>{objective.stage}</span>
            {objective.code ? <span className="objective-card__code">/ {objective.code}</span> : null}
          </span>
          <span className="objective-card__title">
            <HighlightedText text={objective.title} searchTerm={searchTerm} />
          </span>
          {objective.description ? (
            <span className="objective-card__description">
              <HighlightedText text={objective.description} searchTerm={searchTerm} />
            </span>
          ) : null}
          <span className="objective-card__count">
            {objective.resources.length} resource{objective.resources.length === 1 ? "" : "s"}
          </span>
        </span>
        <ChevronIcon className={isOpen ? "objective-card__chevron objective-card__chevron--open" : "objective-card__chevron"} />
      </button>

      <div className={isOpen ? "objective-card__panel objective-card__panel--open" : "objective-card__panel"} id={panelId}>
        <div className="objective-card__resources">
          {objective.resources.length > 0 ? (
            <>
              {objective.topRecommendedResources.length > 0 ? (
                <section className="objective-card__recommended" aria-label="Most recommended resources">
                  <h3>Most recommended resources</h3>
                  <div className="objective-card__recommended-list">
                    {objective.topRecommendedResources.map((resource, index) => (
                      <ResourceCard
                        key={`recommended-${resource.url}-${index}`}
                        resource={resource}
                        enablers={resource.enablerIds.flatMap((code) => enablersByCode.get(code) ?? [])}
                        searchTerm={searchTerm}
                        isTopRecommended
                        recommendationTier={RECOMMENDATION_TIERS[index] ?? "bronze"}
                      />
                    ))}
                  </div>
                </section>
              ) : null}
              {remainingResources.map((resource, index) => (
                <ResourceCard
                  key={`${resource.url}-${index}`}
                  resource={resource}
                  enablers={resource.enablerIds.flatMap((code) => enablersByCode.get(code) ?? [])}
                  searchTerm={searchTerm}
                />
              ))}
            </>
          ) : (
            <p className="objective-card__empty">No resources in this section.</p>
          )}
        </div>
      </div>
    </article>
  );
}
